/**
 * PlanDesignReview: the plan's prototype reviewed where the plan is decided.
 *
 * The case file's Design section opens this in place, above the gate, so the
 * reviewer compares revisions, points and comments, and approves without
 * leaving the ticket. Changes are asked for in the ticket's @yagni sidebar,
 * which revises this prototype and keeps the plan's design on the latest
 * revision while the plan waits on its gate.
 */
import { useEffect, useState } from "react";
import type { PrototypePreviewParts } from "@yagni/shared";
import { PrototypeReview } from "../prototypes/PrototypeReview.js";
import { VIEWPORT_WIDTH, ViewportToggle, type Viewport } from "../prototypes/ViewportToggle.js";
import { Button } from "../ui/index.js";
import { DESIGN_COPY } from "./designCopy.js";
import { OpenInNewTab } from "./OpenInNewTab.js";

export function PlanDesignReview({
  prototypeId,
  title,
  revision,
  approved,
  onClose,
  parts: initialParts,
}: {
  prototypeId: string;
  title: string;
  /** The revision the plan carries. */
  revision: number;
  approved: boolean;
  onClose: () => void;
  /** The revision's parts as the plan's reference carries them; the revisions read refreshes them. */
  parts?: PrototypePreviewParts | null;
}) {
  const [viewport, setViewport] = useState<Viewport>("full");
  const [revisions, setRevisions] = useState<number[]>([revision]);
  const [parts, setParts] = useState<PrototypePreviewParts | null>(initialParts ?? null);

  useEffect(() => {
    const controller = new AbortController();
    void fetch(`/api/prototypes/${encodeURIComponent(prototypeId)}/revisions`, {
      credentials: "include",
      signal: controller.signal,
    })
      .then(async (res) => {
        if (!res.ok) return;
        type Row = { seq: number; parts?: PrototypePreviewParts | null };
        const body = (await res.json()) as { revisions?: Row[] } | Row[];
        const rows = Array.isArray(body) ? body : body.revisions ?? [];
        if (!controller.signal.aborted && rows.length) {
          setRevisions(rows.map((row) => row.seq).sort((a, b) => b - a));
          const current = rows.find((row) => row.seq === revision);
          if (current?.parts) setParts(current.parts);
        }
      })
      // The compare picker keeps the plan's revision alone; the review still works.
      .catch(() => {});
    return () => controller.abort();
  }, [prototypeId, revision]);

  return (
    <div className="cf-design-review" data-testid="plan-design-review">
      <div className="cf-design-review-head">
        <span className="cf-design-sub">{DESIGN_COPY.reviewing(revision)}</span>
        <ViewportToggle value={viewport} onChange={setViewport} />
        <OpenInNewTab prototypeId={prototypeId} revision={revision} />
        <Button size="sm" variant="ghost" onClick={onClose}>
          Close
        </Button>
      </div>
      <p className="cf-design-quiet">
        {approved ? DESIGN_COPY.approvedNotice : DESIGN_COPY.reviewHint}
      </p>
      <PrototypeReview
        key={`${prototypeId}:${revision}`}
        prototypeId={prototypeId}
        title={title}
        revision={revision}
        revisions={revisions}
        width={VIEWPORT_WIDTH[viewport]}
        parts={parts}
      />
    </div>
  );
}
